import React, { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { Broker, Algo } from "../../types";

const getAccountLabel = (broker: any, account: any) => {
  const accountId = String(account.account_id || "");
  const normalizedId = accountId.replace("D#", "");
  
  const accountNames: Record<string, string> = {
    "2109573": "marykate",
    "2304256": "2-9/21",
    "2304257": "3-gold-1k-500",
    "2304258": "4-gold-1k-500",
    "2304259": "5-gold-1k-500",
    "2304262": "6-nq-1k-500",
    "2304263": "7-nq-1k-500",
    "2304264": "8-nq-1k-500",
    "2304265": "9-nq-10k-500",
    "2304266": "10-nq-10k-500"
  };

  const nick = accountNames[normalizedId];
  if (nick) {
    return `${nick} (${account.account_id})`;
  }

  const email = broker.login || "";
  let emailNick = email.split("@")[0] || broker.name || "Account";
  if (emailNick.includes("marykate")) {
    emailNick = "marykate";
  }
  return `${emailNick} - #${account.account_number || account.id} (${account.account_id})`;
};

interface StartInstanceModalProps {
  show: boolean;
  onHide: () => void;
  newInstanceName: string;
  setNewInstanceName: (name: string) => void;
  selectedAccount: string;
  setSelectedAccount: (account: string) => void;
  selectedAlgos: Array<{
    id: number;
    name?: string;
    description?: string;
    parameters?: any;
    parent_algo_id?: number;
    asset_classes?: any[];
    symbols?: any[];
    cash_at_risk?: number;
    returns?: number;
  }>;
  setSelectedAlgos: (
    algos: Array<{
      id: number;
      name?: string;
      description?: string;
      parameters?: any;
      parent_algo_id?: number;
      asset_classes?: any[];
      symbols?: any[];
      cash_at_risk?: number;
      returns?: number;
    }>
  ) => void;
  brokers: Broker[];
  algos: Algo[];
  handleStartInstance: () => Promise<void>;
  // Telegram notification fields
  telegramEnabled: boolean;
  setTelegramEnabled: (enabled: boolean) => void;
  telegramBotToken: string;
  setTelegramBotToken: (token: string) => void;
  telegramChatId: string;
  setTelegramChatId: (chatId: string) => void;
  telegramNotifications: {
    send_entries: boolean;
    send_updates: boolean;
    send_exits: boolean;
    send_system_notifications: boolean;
    send_hourly_status: boolean;
    include_emoji: boolean;
    include_trade_details: boolean;
  };
  setTelegramNotifications: (notifications: any) => void;
  isLoading?: boolean;
}

const StartInstanceModal: React.FC<StartInstanceModalProps> = ({
  show,
  onHide,
  newInstanceName,
  setNewInstanceName,
  selectedAccount,
  setSelectedAccount,
  selectedAlgos,
  setSelectedAlgos,
  brokers = [],
  algos = [],
  handleStartInstance,
  // Telegram notification fields
  telegramEnabled,
  setTelegramEnabled,
  telegramBotToken,
  setTelegramBotToken,
  telegramChatId,
  setTelegramChatId,
  telegramNotifications,
  setTelegramNotifications,
  isLoading = false,
}) => {
  const [tempAlgo, setTempAlgo] = useState<string>("");
  const [isStarting, setIsStarting] = React.useState(false);

  const handleStart = async () => {
    try {
      setIsStarting(true);
      await handleStartInstance();
      onHide();
    } catch (error) {
      console.error("Failed to start instance:", error);
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Start New Trading Instance</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-4">
          <Form.Label>Instance Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter instance name"
            value={newInstanceName}
            onChange={(e) => setNewInstanceName(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>Select Broker Account</Form.Label>
          <Form.Select
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
          >
            <option value="">Choose an account...</option>
            {brokers?.map((broker) =>
              broker.accounts?.map((account) => (
                <option key={account.id} value={account.id}>
                  {getAccountLabel(broker, account)}
                </option>
              ))
            )}
          </Form.Select>
        </Form.Group>

        {(() => {
          const selectedBrokerName = brokers
            ?.find((b) =>
              b.accounts?.some((a) => a.id.toString() === selectedAccount)
            )
            ?.name?.toLowerCase();

          if (
            selectedBrokerName &&
            (selectedBrokerName.includes("interactive") ||
              selectedBrokerName.includes("ibkr"))
          ) {
            return (
              <div className="alert alert-info mb-4">
                Ensure TWS or IB Gateway is running on the localhost port (7497
                for Paper, 7496 for Live) before starting the instance.
              </div>
            );
          }
          return null;
        })()}

        <div className="row mb-4">
          <div className="col">
            <Form.Group>
              <Form.Label>Algorithms</Form.Label>
              <div className="d-flex gap-2 mb-2">
                <Form.Select
                  value={tempAlgo}
                  onChange={(e) => setTempAlgo(e.target.value)}
                >
                  <option value="">Select Algorithm</option>
                  {algos?.map((algo) => (
                    <option key={algo.id} value={algo.id}>
                      {algo.name}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (tempAlgo) {
                      const algo = algos.find(
                        (a) => a.id === parseInt(tempAlgo)
                      );
                      if (algo) {
                        const uniqueId = Date.now().toString().slice(-4); // Last 4 digits of timestamp
                        const uniqueName = `${algo.name}_${newInstanceName}_${uniqueId}`;

                        const allAlgos = [...selectedAlgos];
                        allAlgos.push({
                          id: algo.id,
                          name: uniqueName,
                          description: algo.description,
                          parameters: algo.parameters || {},
                          parent_algo_id: algo.id,
                          asset_classes: algo.asset_classes,
                          symbols: algo.symbols,
                          cash_at_risk: algo.cash_at_risk,
                          returns: algo.returns,
                        });
                        setSelectedAlgos(allAlgos);
                        console.log("selected algos: ", allAlgos);
                        setTempAlgo("");
                      }
                    }
                  }}
                >
                  Add
                </Button>
              </div>
              <div className="border rounded p-3">
                <div className="d-flex flex-column gap-2">
                  {selectedAlgos?.map((linkedAlgo) => {
                    const algo = algos?.find((a) => a.id === linkedAlgo.id);
                    const uniqueName = `${algo?.name}_${newInstanceName}`;
                    return (
                      <div
                        key={linkedAlgo.id}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span>{uniqueName}</span>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setSelectedAlgos(
                              selectedAlgos.filter(
                                (a) => a.id !== linkedAlgo.id
                              )
                            );
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Form.Group>
          </div>
        </div>

        {/* Telegram Notifications Configuration */}
        <div className="mb-4">
          <Form.Group>
            <Form.Check
              type="checkbox"
              label="Enable Telegram Notifications"
              checked={telegramEnabled}
              onChange={(e) => setTelegramEnabled(e.target.checked)}
              className="mb-3"
            />
          </Form.Group>

          {telegramEnabled && (
            <div className="border rounded p-3">
              <h6 className="mb-3">Telegram Configuration</h6>

              <Form.Group className="mb-3">
                <Form.Label>Bot Token</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your Telegram bot token"
                  value={telegramBotToken}
                  onChange={(e) => setTelegramBotToken(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Get this from @BotFather on Telegram
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Chat ID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your Telegram chat ID"
                  value={telegramChatId}
                  onChange={(e) => setTelegramChatId(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Your personal chat ID or group chat ID
                </Form.Text>
              </Form.Group>

              <div className="mb-3">
                <Form.Label>Notification Types</Form.Label>
                <div className="row">
                  <div className="col-md-6">
                    <Form.Check
                      type="checkbox"
                      label="Trade Entries"
                      checked={telegramNotifications.send_entries}
                      onChange={(e) =>
                        setTelegramNotifications({
                          ...telegramNotifications,
                          send_entries: e.target.checked,
                        })
                      }
                    />
                    <Form.Check
                      type="checkbox"
                      label="Trade Updates"
                      checked={telegramNotifications.send_updates}
                      onChange={(e) =>
                        setTelegramNotifications({
                          ...telegramNotifications,
                          send_updates: e.target.checked,
                        })
                      }
                    />
                    <Form.Check
                      type="checkbox"
                      label="Trade Exits"
                      checked={telegramNotifications.send_exits}
                      onChange={(e) =>
                        setTelegramNotifications({
                          ...telegramNotifications,
                          send_exits: e.target.checked,
                        })
                      }
                    />
                    <Form.Check
                      type="checkbox"
                      label="System Notifications"
                      checked={telegramNotifications.send_system_notifications}
                      onChange={(e) =>
                        setTelegramNotifications({
                          ...telegramNotifications,
                          send_system_notifications: e.target.checked,
                        })
                      }
                    />
                  </div>
                  <div className="col-md-6">
                    <Form.Check
                      type="checkbox"
                      label="Hourly Status"
                      checked={telegramNotifications.send_hourly_status}
                      onChange={(e) =>
                        setTelegramNotifications({
                          ...telegramNotifications,
                          send_hourly_status: e.target.checked,
                        })
                      }
                    />
                    <Form.Check
                      type="checkbox"
                      label="Include Emojis"
                      checked={telegramNotifications.include_emoji}
                      onChange={(e) =>
                        setTelegramNotifications({
                          ...telegramNotifications,
                          include_emoji: e.target.checked,
                        })
                      }
                    />
                    <Form.Check
                      type="checkbox"
                      label="Include Trade Details"
                      checked={telegramNotifications.include_trade_details}
                      onChange={(e) =>
                        setTelegramNotifications({
                          ...telegramNotifications,
                          include_trade_details: e.target.checked,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleStart}
          disabled={
            !newInstanceName || !selectedAccount || isStarting || isLoading
          }
        >
          {isStarting || isLoading ? "Starting..." : "Start Instance"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StartInstanceModal;
